import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, X, Mail, Calendar, Hash, CheckCircle2, Clock, ChevronLeft, ChevronRight } from "lucide-react";
import { getApiUrl } from "../../config/api";
import { useAutoDismiss } from "../../hooks/useAutoDismiss";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface Student {
  id: string;
  userCode?: string;
  name: string;
  email: string;
  role: string;
  dateOfBirth?: string | null;
  age?: number | string | null;
  admissionNumber?: string;
  createdAt?: string;
  updatedAt?: string;
  status?: string;
  phone?: string;
  title?: string;
  transportMode?: string;
  transportRouteId?: string;
  className?: string;
  classId?: string;
  currentClassId?: string | number | null;
}

interface TransportRouteOption {
  id: number;
  name: string;
}

interface ClassOption {
  id: number;
  name: string;
}

const calculateAgeFromDate = (dateOfBirth?: string | null) => {
  if (!dateOfBirth) return null;
  const parsed = new Date(`${dateOfBirth}T00:00:00`);
  if (Number.isNaN(parsed.getTime())) return null;

  const today = new Date();
  let age = today.getFullYear() - parsed.getFullYear();
  const monthDelta = today.getMonth() - parsed.getMonth();
  if (monthDelta < 0 || (monthDelta === 0 && today.getDate() < parsed.getDate())) {
    age -= 1;
  }

  return age >= 0 ? age : null;
};

const Students: React.FC = () => {
  const [students, setStudents] = useState<Student[]>([]);
  const [transportRoutes, setTransportRoutes] = useState<TransportRouteOption[]>([]);
  const [availableClasses, setAvailableClasses] = useState<ClassOption[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingStudent, setEditingStudent] = useState<Student | null>(null);
  const [formData, setFormData] = useState({
    name: "",
    title: "",
    dateOfBirth: "",
    email: "",
    phone: "",
    password: "",
    role: "student",
    classId: "",
    transportMode: "personal",
    transportRouteId: "",
  });
  const [message, setMessage] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const token = localStorage.getItem("token");
  const role = (localStorage.getItem("role") || "").toLowerCase();
  const isReadOnly = role === "headteacher";

  useAutoDismiss(message, setMessage, "", 5000);

  const fetchStudents = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("users/students"), {
        headers: { Authorization: `Bearer ${token}` },
      });
      setStudents(res.data.students || []);
    } catch (err: any) {
// Error fetching students
    }
  }, [token]);

  useEffect(() => {
    fetchStudents();
  }, [fetchStudents]);

  useEffect(() => {
    const fetchTransportRoutes = async () => {
      try {
        const res = await axios.get(getApiUrl("transport/routes"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setTransportRoutes(res.data?.routes || []);
      } catch {
        setTransportRoutes([]);
      }
    };

    if (token) {
      fetchTransportRoutes();
    }
  }, [token]);

  useEffect(() => {
    const fetchClasses = async () => {
      try {
        const res = await axios.get(getApiUrl("academics/classes"), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setAvailableClasses(res.data?.classes || []);
      } catch {
        setAvailableClasses([]);
      }
    };

    if (token) {
      fetchClasses();
    }
  }, [token]);

  const filteredStudents = students;
  const calculatedStudentAge = calculateAgeFromDate(formData.dateOfBirth);

  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredStudents.slice(indexOfFirstItem, indexOfLastItem);

  const paginate = (pageNumber: number) => setCurrentPage(pageNumber);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    if (name === "transportRouteId") {
      setFormData((prev) => ({
        ...prev,
        transportRouteId: value,
        transportMode: value ? "school" : "personal",
      }));
      return;
    }
    setFormData({ ...formData, [name]: value });
  };

  const openAddModal = () => {
    setEditingStudent(null);
    setFormData({
      name: "",
      title: "",
      dateOfBirth: "",
      email: "",
      phone: "",
      password: "",
      role: "student",
      classId: "",
      transportMode: "personal",
      transportRouteId: "",
    });
    setIsModalOpen(true);
  };

  const openEditModal = (student: Student) => {
    setEditingStudent(student);
    setFormData({
      name: student.name,
      title: student.title || "",
      dateOfBirth: student.dateOfBirth || "",
      email: student.email,
      phone: student.phone || "",
      password: "",
      role: student.role,
      classId: student.currentClassId ? String(student.currentClassId) : (student.classId || ""),
      transportMode: student.transportMode || "personal",
      transportRouteId: student.transportRouteId || "",
    });
    setIsModalOpen(true);
  };

  const handleDeleteStudent = async (id: string) => {
    if (!window.confirm("Are you sure you want to delete this student?")) return;
    try {
      await axios.delete(getApiUrl(`users/${id}`), {
        headers: { Authorization: `Bearer ${token}` },
      });
      fetchStudents();
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Error deleting student");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingStudent) {
        await axios.put(
          getApiUrl(`users/${editingStudent.id}`),
          {
            ...formData,
            dateOfBirth: formData.dateOfBirth || null,
            currentClassId: formData.classId || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      } else {
        await axios.post(
          getApiUrl("auth/signup"),
          {
            ...formData,
            dateOfBirth: formData.dateOfBirth || null,
            currentClassId: formData.classId || null,
          },
          { headers: { Authorization: `Bearer ${token}` } }
        );
      }
      fetchStudents();
      setIsModalOpen(false);
    } catch (err: any) {
      setMessage(err.response?.data?.message || "Error saving student");
    }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="utopia-page-container">
          <div className="utopia-table-header">
            <div>
              <h2>Students Directory</h2>
              <p className="page-subtitle">View and manage all enrolled students</p>
            </div>
            {!isReadOnly && (
              <button
                className="utopia-btn-primary flex items-center gap-2"
                onClick={openAddModal}
              >
                <Plus size={18} /> Add Student
              </button>
            )}
          </div>

          <table className="utopia-table">
            <thead>
              <tr>
                <th>Student Information</th>
                <th>Email</th>
                <th>Phone Number</th>
                <th>Account Status</th>
                {!isReadOnly && <th className="text-right pr-6">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {currentItems.map((s) => {
                const admissionDisplay = s.admissionNumber || s.userCode || String(s.id).slice(-6).toUpperCase();
                const createdDate = s.createdAt ? new Date(s.createdAt) : null;
                const updatedDate = s.updatedAt ? new Date(s.updatedAt) : null;
                const status = s.status || 'active';
                
                return (
                  <tr key={s.id} className="group">
                    <td>
                      <div className="utopia-info-cell">
                        <div className="flex items-center gap-3 mb-2">
                          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-teal-50 to-teal-100 flex items-center justify-center text-teal-600 font-bold text-sm border-2 border-teal-200">
                            {s.name.charAt(0).toUpperCase()}
                          </div>
                          <div className="flex-1">
                            <span className="utopia-table-cell-primary block">{s.name}</span>
                            <div className="utopia-info-row mt-1">
                              <Hash size={10} className="text-slate-400" />
                              <span className="utopia-table-cell-meta">Admission No: {admissionDisplay}</span>
                            </div>
                            <div className="utopia-info-row mt-1">
                              <Calendar size={10} className="text-slate-400" />
                              <span className="utopia-table-cell-meta">
                                Age: {s.age ?? calculateAgeFromDate(s.dateOfBirth) ?? "Not recorded"}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="utopia-info-cell">
                        <div className="utopia-info-row mb-2">
                          <Mail size={12} className="text-slate-400" />
                          <span className="utopia-table-cell-primary text-sm">{s.email}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="utopia-info-cell">
                        <div className="utopia-info-row mb-1">
                          <span className="utopia-table-cell-secondary text-xs">{s.phone || "N/A"}</span>
                        </div>
                      </div>
                    </td>
                    <td>
                      <div className="utopia-info-cell">
                        <div className="mb-2">
                          <span className={`utopia-badge ${status === 'active' ? 'bg-teal-50 text-teal-700 border-teal-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                            {s.role}
                          </span>
                        </div>
                        <div className="utopia-info-row">
                          {status === 'active' ? (
                            <>
                              <CheckCircle2 size={10} className="text-teal-500" />
                              <span className="utopia-table-cell-meta text-teal-600">Active Account</span>
                            </>
                          ) : (
                            <>
                              <Clock size={10} className="text-slate-400" />
                              <span className="utopia-table-cell-meta">Inactive</span>
                            </>
                          )}
                        </div>
                      </div>
                    </td>
                    {!isReadOnly && (
                      <td className="text-right pr-6">
                        <div className="flex justify-end gap-2">
                          <button 
                            className="utopia-btn-icon hover:bg-teal-50 hover:text-teal-600 text-slate-400 transition-all" 
                            onClick={() => openEditModal(s)}
                            title="Edit Student"
                          >
                            <Edit2 size={16} />
                          </button>
                          <button 
                            className="utopia-btn-icon text-red-500 hover:text-red-700 hover:bg-red-50 transition-all" 
                            onClick={() => handleDeleteStudent(s.id)}
                            title="Delete Student"
                          >
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>

          {totalPages > 1 && (
            <div className="utopia-pagination">
              <button
                className="utopia-page-btn utopia-page-btn-nav"
                disabled={currentPage === 1}
                onClick={() => paginate(currentPage - 1)}
                aria-label="Previous page"
              >
                <ChevronLeft size={16} />
              </button>
              
              {(() => {
                const pages: (number | string)[] = [];
                const maxVisible = 5;
                
                if (totalPages <= maxVisible) {
// Show all pages if total is small
                  for (let i = 1; i <= totalPages; i++) {
                    pages.push(i);
                  }
                } else {
// Always show first page
                  pages.push(1);
                  
                  if (currentPage > 3) {
                    pages.push('...');
                  }
// Show pages around current page
                  const start = Math.max(2, currentPage - 1);
                  const end = Math.min(totalPages - 1, currentPage + 1);
                  
                  for (let i = start; i <= end; i++) {
                    if (i !== 1 && i !== totalPages) {
                      pages.push(i);
                    }
                  }
                  
                  if (currentPage < totalPages - 2) {
                    pages.push('...');
                  }
// Always show last page
                  pages.push(totalPages);
                }
                
                return pages.map((p, idx) => {
                  if (p === '...') {
                    return (
                      <span key={`ellipsis-${idx}`} className="utopia-pagination-ellipsis">
                        ...
                      </span>
                    );
                  }
                  return (
                    <button
                      key={p}
                      className={`utopia-page-btn ${currentPage === p ? 'active' : ''}`}
                      onClick={() => paginate(p as number)}
                    >
                      {p}
                    </button>
                  );
                });
              })()}
              
              <button
                className="utopia-page-btn utopia-page-btn-nav"
                disabled={currentPage === totalPages}
                onClick={() => paginate(currentPage + 1)}
                aria-label="Next page"
              >
                <ChevronRight size={16} />
              </button>
              
              <div className="utopia-pagination-info">
                <span>
                  Showing <strong>{indexOfFirstItem + 1}</strong> - <strong>{Math.min(indexOfLastItem, filteredStudents.length)}</strong> of <strong>{filteredStudents.length}</strong>
                </span>
              </div>
            </div>
          )}
        </div>
      </main>

      {/* Add/Edit Modal */}
      {isModalOpen && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}>
          <div className="utopia-modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <h3 className="text-lg font-bold">{editingStudent ? "Edit Student" : "Add New Student"}</h3>
              <button 
                className="utopia-modal-close-btn" 
                onClick={() => setIsModalOpen(false)}
                aria-label="Close modal"
              >
                <X size={18} />
              </button>
            </div>
            <form onSubmit={handleSubmit} onKeyDown={(e) => e.key === 'Escape' && setIsModalOpen(false)}>
              {message && (
                <div className={`${message.includes('Error') ? 'utopia-form-error' : 'utopia-form-success'} mb-4`}>
                  {message}
                </div>
              )}
              <div className="utopia-form-group">
                <label className="utopia-label required">Full Name</label>
                <input 
                  type="text" 
                  name="name" 
                  className="utopia-input" 
                  placeholder="Enter full name (e.g. John Doe)" 
                  value={formData.name} 
                  onChange={handleChange} 
                  required 
                  autoFocus
                />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">User Role</label>
                <select
                  name="role"
                  className="utopia-input"
                  value={formData.role}
                  onChange={handleChange}
                  required
                >
                  <option value="student">Student</option>
                </select>
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">Gender</label>
                <input
                  type="text"
                  name="title"
                  list="student-gender-options"
                  className="utopia-input"
                  placeholder="Male or Female"
                  value={formData.title}
                  onChange={handleChange}
                  required
                />
                <datalist id="student-gender-options">
                  <option value="Male" />
                  <option value="Female" />
                </datalist>
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">Email Address</label>
                <input 
                  type="email" 
                  name="email" 
                  className="utopia-input" 
                  placeholder="student@school.com" 
                  value={formData.email} 
                  onChange={handleChange} 
                  required 
                />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">Phone Number</label>
                <input 
                  type="tel" 
                  name="phone" 
                  className="utopia-input" 
                  placeholder="+260971234567" 
                  value={formData.phone} 
                  onChange={handleChange} 
                  required 
                />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">Date of Birth</label>
                <input
                  type="date"
                  name="dateOfBirth"
                  className="utopia-input"
                  value={formData.dateOfBirth}
                  onChange={handleChange}
                  required
                />
                <p className="text-xs text-slate-500 mt-2">
                  {calculatedStudentAge === null
                    ? "This is stored for report cards and age-based records."
                    : `Calculated age: ${calculatedStudentAge} year${calculatedStudentAge === 1 ? "" : "s"}.`}
                </p>
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label">Class Assignment</label>
                <select
                  name="classId"
                  className="utopia-input"
                  value={formData.classId}
                  onChange={handleChange}
                >
                  <option value="">No class assigned yet</option>
                  {availableClasses.map((cls) => (
                    <option key={cls.id} value={cls.id}>
                      {cls.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">Transport Route</label>
                <select
                  name="transportRouteId"
                  className="utopia-input"
                  value={formData.transportRouteId}
                  onChange={handleChange}
                >
                  <option value="">Personal (No school transport)</option>
                  {transportRoutes.map((route) => (
                    <option key={route.id} value={route.id}>
                      {route.name}
                    </option>
                  ))}
                </select>
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">
                  Password {editingStudent ? "(leave blank to keep current)" : ""}
                </label>
                <input 
                  type="password" 
                  name="password" 
                  className="utopia-input" 
                  placeholder={editingStudent ? "Enter new password or leave blank" : "Enter secure password"}
                  value={formData.password} 
                  onChange={handleChange} 
                  required={!editingStudent}
                  minLength={editingStudent && !formData.password ? undefined : 8}
                />
              </div>
              <div className="flex gap-3 mt-4">
                <button 
                  type="button" 
                  className="utopia-btn-secondary flex-1 py-3" 
                  onClick={() => setIsModalOpen(false)}
                >
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary flex-1 py-3">
                  {editingStudent ? "Save Changes" : "Register Student"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Students;
